// USAGE:
// 1. Download the SVG icon from Figma and add it to the app/client/src/icons directory
// 2. The icon's name should be the filename of the SVG file (e.g. "Icon Name.svg")
// 3. Replace instances of color codes with "currentColor"
//
// For icons with secondary colors, replace the fill/stroke with "var(--secondary-color, currentColor)" in the SVG file

import { COLORS } from "@constants";
import * as Icons from "@icons";
import { IconName } from "@tsTypes/icons";
import { Size } from "@tsTypes/ui";
import styled from "styled-components";

export interface IconProps {
  name: IconName;
  color?: string;
  secondaryColor?: string;
  size?: Size | number;
  margin?: string | 0;
  display?: string;
  "data-testid"?: string;
  className?: string;
}

const SIZES = {
  xxs: 10,
  xs: 16,
  sm: 20,
  md: 24,
  lg: 32,
  xl: 40,
  xxl: 56,
};

const Icon = ({
  name,
  color = COLORS.BLACK,
  secondaryColor,
  size = "md",
  margin = 0,
  display = "flex",
  "data-testid": dataTestId,
  className,
}: IconProps) => {
  const formattedName = formatIconName(name);
  const SvgComponent = Icons[formattedName as keyof typeof Icons];

  const numericSize = typeof size === "number" ? size : SIZES[size];

  return (
    <IconContainer
      size={numericSize}
      margin={margin}
      display={display}
      data-testid={dataTestId ?? `${name}-icon`}
      color={color}
      secondaryColor={secondaryColor}
      className={className}
    >
      <SvgComponent width={numericSize} height={numericSize} />
    </IconContainer>
  );
};

export default Icon;

const IconContainer = styled.div<{ color: string; secondaryColor?: string }>`
  height: ${({ size }) => `${size}px`};
  width: ${({ size }) => `${size}px`};
  margin: ${({ margin }) => margin};
  display: ${({ display }) => display};
  color: ${({ color }) => color};
  ${({ secondaryColor }) => secondaryColor && `--secondary-color: ${secondaryColor};`}
  align-items: center;
`;

function formatIconName(name: string): string {
  let formattedName = name
    .replace(/&/g, "")
    .replace(/-/g, " ")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join("");

  // Handle special case for icons starting with numbers (like "3D Printing" -> "_3DPrinting")
  if (/^\d/.test(name)) {
    formattedName = "_" + formattedName;
  }

  return formattedName;
}
